<template>
  <div class="card">
    <div class="row">
      <div class="col-xs-12 col-md-6">
        <div class="card aligner-wrapper">
          <div class="card-body">
            <div
              class="absolute left top bottom h-60 v-strock-2 bg-primary"
              :class="boxEquipmentType ? 'bg-info' : 'bg-primary'"
            ></div>
            <p class="text-muted mb-2">
              {{ $t('dashboards.discreteValuePlot.counterAllTime') }}
              <br />
              <span class="smallText">
                {{ $t('dashboards.discreteValuePlot.finishedProducts') }}
              </span>
            </p>

            <div class="d-xl-flex align-items-center">
              <h1 class="font-weight-medium mb-2">{{ `${lastValue}` }}</h1>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xs-12 col-md-6">
        <div class="card aligner-wrapper">
          <div class="card-body">
            <div
              class="absolute left top bottom h-60 v-strock-2"
              :class="boxEquipmentType ? 'bg-info' : 'bg-primary'"
            ></div>
            <p class="text-muted mb-2">
              {{ $t('dashboards.discreteValuePlot.counterPeriod') }}
              {{ tabPeriodText }}
              <br />
              <span class="smallText">
                {{ $t('dashboards.discreteValuePlot.finishedProducts') }}
              </span>
            </p>
            <div class="d-xl-flex align-items-center">
              <h1 class="font-weight-medium mb-2">{{ `${overallValue}` }}</h1>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div v-if="loading" class="dot-opacity-loader">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <div id="counterChartContainer" style="height: 300px"></div>
    </div>
  </div>
</template>

<script>
import sensorValues from '@/api/boxinfo/sensorValues.gql';
import sensorLastValue from '@/api/boxinfo/sensorLastValue.gql';
import moment from 'moment';
import CanvasJS from '@/plugins/canvasjs.min';
const canvas = CanvasJS.Chart ? CanvasJS : window.CanvasJS;

export default {
  name: 'CounterValuesPlot',
  props: {
    sensorId: Number,
    period: Object,
    tabPeriodText: String,
    boxEquipmentType: Number,
    minPeriod: Number,
    maxPeriod: Number,
  },
  apollo: {
    sensorData: {
      query: sensorValues,
      variables() {
        return {
          id: this.sensorId,
          startTime: this.start,
          endTime: this.end,
        };
      },
      pollInterval: 30000,
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        return ((queryData || {}).sensor || [])[0] || {};
      },
      skip() {
        return !this.sensorId || !this.period || !this.period.dateStart;
      },

      fetchPolicy: 'cache-and-network',
    },
    sensorLastValue: {
      query: sensorLastValue,
      variables() {
        return {
          id: this.sensorId,
        };
      },
      pollInterval: 30000,
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        const sensor =
          queryData && queryData.sensor && queryData.sensor.length ? queryData.sensor[0] : {};
        return sensor && sensor.lastValue && sensor.lastValue.value ? sensor.lastValue.value : 0.0;
      },
      skip() {
        return !this.sensorId;
      },

      fetchPolicy: 'cache-and-network',
    },
  },
  data: () => {
    return {
      operValueType: 1,
      sensorLastValue: 0.0,
    };
  },
  computed: {
    start() {
      return this.rangeMin
        ? moment(this.rangeMin).format()
        : this.minPeriod
        ? moment(this.minPeriod).format()
        : this.period.dateStart;
    },
    end() {
      return this.rangeMax
        ? moment(this.rangeMax).format()
        : this.maxPeriod
        ? moment(this.maxPeriod).format()
        : this.period.dateEnd;
    },
    tabNames() {
      return [this.$t('box.target'), this.$t('box.cumulative')];
    },
    currentOperValCaption() {
      return `${this.tabNames[0]}`;
    },
    allOperValCaption() {
      return `${this.tabNames[1]}`;
    },
    unitName() {
      return (
        (((this.sensorData || {}).type || {}).measurementUnit || {}).shortName ||
        this.$t('dashboards.operationValuePlot.units')
      );
    },
    values() {
      return (this.sensorData || {}).values || [];
    },
    showValues() {
      const values = this.values;
      const firstValue = ((values[0] || {}).value || 0) - 1;
      values.forEach((element) => {
        element.value -= firstValue;
      });
      return values;
    },
    lastValue() {
      const total = this.sensorLastValue;
      return total || this.$t('dashboards.noData');
    },
    overallValue() {
      let total = this.values.length
        ? this.values[this.values.length - 1].value - this.values[0].value + 1
        : 0;
      return total || this.$t('dashboards.noData');
    },
    dataSeries() {
      const points = this.showValues.map((i) => {
        return {
          x: new Date(moment(i.timeStamp).valueOf()),
          y: i.value,
        };
      });
      return [
        {
          type: 'splineArea',
          color: '#8F5FE8',
          dataPoints: points,
        },
      ];
    },
    chartOptionsCj() {
      const vm = this;
      return {
        theme: 'dark2',
        zoomEnabled: true,
        zoomType: 'x',
        backgroundColor: 'rgb(25, 28, 36)',
        axisX: {
          minimum: vm.minDate,
          maximum: vm.maxDate,
          lineThickness: 1,
          gridThickness: 0,
          lineColor: 'grey',
          labelFontColor: 'grey',
          tickColor: 'grey',
          labelMaxWidth: 40,
          labelFontSize: 10,
          labelFormatter: function (e) {
            const label = moment(e.value).locale(vm.locale).format('DD MMM HH:mm');

            return label;
          },
        },
        axisY: {
          minimum: 0,
          lineThickness: 0,
          tickThickness: 0,
          gridColor: '#000000',
          labelFontColor: 'grey',
          includeZero: true,
        },
        toolTip: {
          contentFormatter: function (e) {
            const value = `${vm.formatNumber(e.entries[0].dataPoint.y)} ${vm.unitName}`;
            const start = moment(e.entries[0].dataPoint.x)
              .locale(vm.locale)
              .format('DD MMM HH:mm:ss');
            return `${start}: <b><font color="green">${value}</font></b>`;
          },
        },
        rangeChanged: function (e) {
          vm.rangeMin = e.axisX[0].viewportMinimum;
          vm.rangeMax = e.axisX[0].viewportMaximum;
        },
        data: vm.dataSeries,
      };
    },
    minDate() {
      return this.minPeriod
        ? this.minPeriod
        : this.period.dateStart
        ? new Date(this.period.dateStart).getTime()
        : new Date().getTime();
    },
    maxDate() {
      return this.maxPeriod
        ? this.maxPeriod
        : this.period.dateEnd
        ? new Date(this.period.dateEnd).getTime()
        : new Date().getTime();
    },
    loading() {
      return this.$apollo.queries.sensorData.loading;
    },
    locale() {
      return this.$i18n.locale;
    },
  },
  updated() {
    this.updateChart();
  },
  methods: {
    updateChart() {
      const chartId = 'counterChartContainer';
      if (document.getElementById(chartId)) {
        this.chart = new canvas.Chart(chartId, this.chartOptionsCj);
        this.chart.render();
      }
    },
    formatNumber(v) {
      return Number(v.toPrecision(4)).toLocaleString();
    },
  },
};
</script>

<style scoped>
#wrapper {
  position: block;
  background: white;
  border: 1px solid #000;
  box-shadow: 0 22px 35px -16px rgba(0, 0, 0, 0.71);
  height: 200px !important;
  min-height: 200px !important;
  max-height: 300px !important;
  margin: 0 auto;
}
#chart-bar {
  position: relative;
  margin-top: -70px;
}
.tab-solid {
  margin-bottom: 0px !important;
}
.dot-opacity-loader {
  position: absolute;
  height: 25px;
  left: calc(50% - 50px);
}
.dot-opacity-loader span {
  background-color: #0090e7;
  margin: 5px 5px;
}
</style>
